import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Tour,
  type TourStep,
  useTour,
} from "@frontend/components/organisms/Tour";
import { useLingui } from "@lingui/react/macro";

interface DateRecurrenceTourProps {
  scope: string;
  schedule: boolean;
  exceptions: boolean;
  onRunningChange?: (running: boolean) => void;
}

export function DateRecurrenceTour({
  scope,
  schedule,
  exceptions,
  onRunningChange,
}: DateRecurrenceTourProps): JSX.Element {
  const { t } = useLingui();

  const label = t`Guided tour`;

  const walkthrough: (TourStep | false)[] = [
    {
      icon: "Repeat",
      title: t`One panel, one rule`,
      content: t`Everything here describes a single recurrence rule. Nothing is written until you touch a control.`,
    },
    schedule && {
      target: `${scope} [data-slot="date-recurrence-schedule"]`,
      icon: "CalendarRange",
      title: t`When it starts, when it stops`,
      content: t`The start date belongs to the record around the picker. The end is part of the rule itself, as a date or a number of occurrences.`,
      side: "bottom",
    },
    {
      target: `${scope} [data-slot="tabs-list"]`,
      icon: "Repeat2",
      title: t`The frequency`,
      content: t`Daily, weekly, monthly, yearly. Switching tab rewrites the rule, so the options below always belong to the frequency on screen.`,
      side: "bottom",
    },
    {
      target: `${scope} [data-slot="tabs-panel"]`,
      icon: "CalendarDays",
      title: t`The frequency's own options`,
      content: t`Every N months, on a set of days or on a position such as the second Saturday. The sentence at the top reads as the rule does.`,
      side: "right",
    },
    exceptions && {
      target: `${scope} [data-slot="date-recurrence-exceptions"]`,
      icon: "CalendarOff",
      title: t`The exceptions`,
      content: t`Spans the rule skips: the school holidays, one period, the same period every year, or a number of weeks.`,
      side: "top",
    },
  ];

  const steps = walkthrough.filter((step): step is TourStep => false !== step);

  const tour = useTour({
    steps,
    onStart: () => onRunningChange?.(true),
    onComplete: () => onRunningChange?.(false),
    onSkip: () => onRunningChange?.(false),
  });

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="sm"
              aria-label={label}
              onClick={() => tour.start()}
              leftSection={<Icon name="CircleHelp" size="sm" />}
            />
          }
        />

        <TooltipContent>{label}</TooltipContent>
      </Tooltip>

      <Tour {...tour.props} />
    </>
  );
}
